import { Clickable, OnEscapeListener } from '@carvana/merch-components';
import cx from 'classnames';
import { Settings } from 'lucide-react';
import { useCallback, useState } from 'react';
import FocusLock from 'react-focus-lock';
import Vehicles from '@/common/PassengerTools/Vehicles';
import ZipCodes from '@/common/PassengerTools/ZipCodes';
import { BranchName } from './BranchName';
import Dealerships from './Dealerships';
import Experiments from './Experiments';
import ShareLink from './ShareLink';
import Users from './Users';

type MainView =
  | 'branch'
  | 'dealerships'
  | 'experiments'
  | 'shareLink'
  | 'users'
  | 'vehicles'
  | 'zip';

const tabs: Array<{ id: MainView; label: string; color: string }> = [
  { id: 'branch', label: 'Feature Branch', color: 'bg-gray-600' },
  { id: 'users', label: 'Users', color: 'bg-blue-500' },
  { id: 'vehicles', label: 'Vehicles', color: 'bg-red-500' },
  { id: 'zip', label: 'Zip', color: 'bg-[#00874a]' },
  { id: 'experiments', label: 'Experiments', color: 'bg-[#9d00ff]' },
  { id: 'dealerships', label: 'Dealerships', color: 'bg-orange-500' },
  { id: 'shareLink', label: 'FB Link', color: 'bg-brand-secondary' }
];

type OwnProps = {
  setShowModal: (show: boolean) => void;
};

export default function PassengerTools({ setShowModal }: OwnProps) {
  const [currentView, setCurrentView] = useState<MainView>('branch');

  const handleClose = useCallback(() => {
    setShowModal(false);
    setCurrentView('branch');
  }, [setShowModal]);

  return (
    <FocusLock autoFocus={false}>
      <OnEscapeListener onEscape={handleClose}>
        <div
          className="fixed inset-0 z-[99999] bg-grey-5/50"
          onClick={handleClose}
          data-analytics-id={null}
          data-testid="passenger-tools-backdrop"
        />
        <div className="fixed top-0 z-[99999] flex w-full p-lg">
          <div className="mx-auto flex w-[700px] max-w-[calc(100vw-32px)] flex-col gap-lg rounded-lg bg-[#2a2a2a] p-xl shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-700">
              <h2 className="flex flex-1 items-center gap-sm text-white t-heading-md">
                <Settings size={20} /> Passenger
              </h2>

              <Clickable
                element="button"
                onClick={handleClose}
                className="flex h-32 w-32 items-center justify-center rounded-full bg-gray-700 text-gray-300 transition-colors hover:bg-gray-600 hover:text-white"
                data-analytics-id={null}
                data-testid="close-passenger-tools"
                title="Close"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </Clickable>
            </div>

            <div className="flex flex-wrap gap-xs border-b border-gray-700 pb-sm m:gap-sm">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setCurrentView(tab.id);
                  }}
                  className={cx(
                    'shrink-0 rounded-t px-sm py-xs text-white transition-all t-body-xs m:px-lg m:py-sm m:t-body-sm',
                    currentView === tab.id
                      ? `${tab.color} opacity-100 shadow-md`
                      : 'bg-gray-700 opacity-70 hover:opacity-90'
                  )}
                  data-analytics-id={null}
                  data-testid="passenger-tools-tab"
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {currentView === 'branch' && <BranchName />}
              {currentView === 'shareLink' && <ShareLink />}
              {currentView === 'users' && <Users />}
              {currentView === 'vehicles' && <Vehicles />}
              {currentView === 'zip' && <ZipCodes />}
              {currentView === 'experiments' && (
                <Experiments onClose={handleClose} />
              )}
              {currentView === 'dealerships' && <Dealerships />}
            </div>
          </div>
        </div>
      </OnEscapeListener>
    </FocusLock>
  );
}
